/**
 * Main AlterConnect SDK class
 *
 * Opens the backend-served Connect UI in a popup (or, when `container` is
 * supplied, renders it inline in an iframe). The backend handles provider
 * selection, branding, and OAuth — then sends the result back via postMessage.
 */

import type {
  AlterConnectConfig,
  OpenOptions,
  Grant,
  AlterError,
  ConnectCompletion,
  ConnectSessionKind,
} from "../types";
import {
  validateConfig,
  mergeConfig,
  debugLog,
  DEFAULT_ALTER_HOST,
  type FullConfig,
} from "./config";
import { EventEmitter, type EventHandler } from "./events";
import { StateManager } from "../state/manager";
import { OAuthHandler } from "../oauth/handler";
import { DirectWindowHandler } from "../oauth/direct-window-handler";
import { EmbedHandler } from "../oauth/embed-handler";

/**
 * SDK version
 */
// Must equal the version in package.json: getVersion() is public API, and a
// version accessor that reports anything else is a lie to the caller. The
// pairing is pinned by sdk_tests/version.test.ts, without which this constant
// sat at 0.2.0 through twelve minor releases.
const VERSION = "0.17.1";

/**
 * Module-scoped flag so the non-default-host warning fires at most once
 * per process. Reset is intentionally not exposed — the warning exists to
 * surface accidental misconfigurations during dev, not to spam logs.
 */
let hasWarnedNonDefaultHost = false;

/**
 * Which hosted Connect page a session token opens, from its prefix: `ms_sess_`
 * (a managed-secret session, `ManagedSecretConnectSession.sessionToken` in the
 * server SDKs) or `sess_` (an OAuth Connect session). `null` for anything
 * else, which no Alter backend issues.
 */
export function connectSessionKind(token: string): ConnectSessionKind | null {
  if (token.startsWith("ms_sess_")) return "managed_secret";
  if (token.startsWith("sess_")) return "oauth";
  return null;
}

/**
 * Compose the hosted Connect UI URL.
 *
 * The session token rides in the FRAGMENT so it never reaches a server log or
 * a Referer. Inline mode adds the framing page's origin as a query parameter:
 * the backend serves the cookie-less binding shell frameable by exactly that
 * origin, and the bind then refuses it unless it equals the origin the
 * application bound to the session when it was created.
 *
 * The session's kind picks the page (`/sdk/oauth/connect` or
 * `/sdk/managed-secrets/connect`); both take the same `bind=1`, `embed=1` /
 * `embed_origin` and `#session=` grammar.
 */
function buildConnectUrl(
  baseURL: string,
  token: string,
  kind: ConnectSessionKind,
  embedOrigin?: string,
): string {
  const page =
    kind === "managed_secret"
      ? `${baseURL}/sdk/managed-secrets/connect`
      : `${baseURL}/sdk/oauth/connect`;
  const fragment = `#session=${encodeURIComponent(token)}`;
  if (embedOrigin === undefined) {
    return `${page}?bind=1${fragment}`;
  }
  return `${page}?bind=1&embed=1&embed_origin=${encodeURIComponent(embedOrigin)}${fragment}`;
}

/**
 * Main SDK class
 */
export class AlterConnect {
  private config: FullConfig;
  private eventEmitter: EventEmitter;
  private stateManager: StateManager;
  private _isInitialized: boolean;
  private _flowHandler:
    | OAuthHandler
    | EmbedHandler
    | DirectWindowHandler
    | null = null;
  private readonly _baseURL: string;
  private _perOpenCleanups: Array<() => void> = [];

  /**
   * The element that had focus when `open()` was called. An inline flow
   * mounts DOM in the host page and removes it again on close; when the user
   * had tabbed into that DOM, its removal drops focus to `<body>` and a
   * keyboard user loses their place (WCAG 2.4.3). `close()` hands focus back
   * to this element if focus fell to the body. Null when nothing is owed.
   */
  private _focusReturnTarget: HTMLElement | null = null;

  /**
   * A redirect-return outcome that arrived before anyone could listen.
   * See `deliverRedirectOutcome`. Null once delivered.
   */
  private pendingRedirectOutcome: {
    event: "success" | "error";
    args: unknown[];
  } | null = null;

  /**
   * Private constructor (use AlterConnect.create() instead)
   */
  private constructor(config: AlterConnectConfig = {}) {
    // Validate and merge config
    validateConfig(config);
    this.config = mergeConfig(config);

    // Resolve the Alter host once at construction time. `baseURL` is an
    // environment-level setting (production vs self-hosted vs local-dev),
    // not a per-`open()` decision — that's why it lives on the constructor
    // config and not on OpenOptions.
    this._baseURL = (config.baseURL ?? DEFAULT_ALTER_HOST).replace(/\/+$/, "");
    if (this._baseURL !== DEFAULT_ALTER_HOST && !hasWarnedNonDefaultHost) {
      hasWarnedNonDefaultHost = true;
      // Log only the origin, not the full URL. validateConfig rejects
      // userinfo/search/hash, so the path is the only "extra" piece the
      // origin strips — but it costs us nothing to be defensive here in
      // case validateConfig's allowlist is ever broadened. The warn
      // message is forwarded to customer-side error trackers (Sentry,
      // Datadog RUM), so minimizing what flows through it is prudent.
      const origin = new URL(this._baseURL).origin;
      console.warn(
        `[alter-connect] Using non-default Alter host: ${origin}. ` +
          "Unset baseURL in AlterConnect.create() for production.",
      );
    }

    // Initialize components
    this.eventEmitter = new EventEmitter();
    this.stateManager = new StateManager();
    this._isInitialized = true;

    debugLog(this.config, "Alter Connect SDK initialized", {
      version: VERSION,
      baseURL: this._baseURL,
    });

    // Check if returning from OAuth redirect (mobile flow)
    this.checkRedirectReturn();
  }

  /**
   * Check if returning from OAuth redirect and handle result
   * This runs automatically on SDK initialization
   */
  private checkRedirectReturn(): void {
    const handled = OAuthHandler.checkOAuthReturn(
      (grants: Grant[], completion: ConnectCompletion) => {
        debugLog(this.config, "OAuth redirect return - success:", grants);
        this.deliverRedirectOutcome("success", [grants, completion]);
      },
      (error: AlterError) => {
        debugLog(this.config, "OAuth redirect return - error:", error);
        this.deliverRedirectOutcome("error", [error]);
      },
    );

    if (handled) {
      debugLog(this.config, "OAuth redirect return detected and handled");
    }
  }

  /**
   * Deliver a redirect-return outcome, RETAINING it until someone listens.
   *
   * This runs from the constructor, which runs inside `AlterConnect.create()`.
   * A plain `emit()` here therefore fires before `create()` even returns — the
   * caller does not hold the instance yet, so it cannot have called `.on()`,
   * and the constructor config accepts no callbacks. Every mobile redirect
   * outcome was computed correctly and dropped into an empty emitter. (The old
   * comment claimed it "will be picked up by event handlers if SDK was
   * re-initialized"; nothing replayed it, so no ordering ever worked.)
   *
   * Deferring to a microtask is not enough: it only rescues a listener
   * registered synchronously after `create()`, and an app that awaits anything
   * first — a session fetch, a router guard, hydration — still misses it.
   *
   * So the outcome is held and handed to the FIRST listener for that event,
   * whenever it arrives. This is the same contract as Firebase Auth's
   * `getRedirectResult()`: the redirect result is a value you can ask for at
   * any time, not a moment you had to be present for.
   *
   * It is delivered ONCE. A retained outcome that replayed to every future
   * listener would fire a stale "connected!" on a later, unrelated
   * subscription — worse than the bug this fixes.
   */
  private deliverRedirectOutcome(
    event: "success" | "error",
    args: unknown[],
  ): void {
    if (this.eventEmitter.hasListeners(event)) {
      this.eventEmitter.emit(event, ...args);
      return;
    }
    this.pendingRedirectOutcome = { event, args };
  }

  /**
   * Hand a retained redirect outcome to a listener that has just subscribed.
   * Called from `on()`; consumed exactly once.
   */
  private flushRedirectOutcome(event: string): void {
    const pending = this.pendingRedirectOutcome;
    if (pending === null || pending.event !== event) return;
    this.pendingRedirectOutcome = null;
    this.eventEmitter.emit(pending.event, ...pending.args);
  }

  /**
   * Create a new AlterConnect instance
   *
   * @param config Optional configuration
   * @returns AlterConnect instance
   *
   * @example
   * const alterConnect = AlterConnect.create();
   * const alterConnect = AlterConnect.create({ debug: true });
   */
  static create(config?: AlterConnectConfig): AlterConnect {
    return new AlterConnect(config);
  }

  /**
   * Open the Connect UI — in a popup window, or inline when `container` is set
   *
   * Opens Alter Connect. Alter handles provider selection, branding, and the
   * OAuth flow, then reports the typed completion to this application.
   *
   * @param options Configuration including the session token from your backend
   *
   * @example
   * // Inline (embedded) mode: render inside an element you size.
   * await alterConnect.open({
   *   token: session_token,
   *   container: document.getElementById('alter-connect')!,
   *   onSuccess: (grants) => console.log('Connected!', grants),
   *   onExit: () => console.log('User cancelled'),
   * });
   *
   * @example
   * // 1. Get session token from YOUR backend
   * const { session_token } = await fetch('/api/alter/create-session').then(r => r.json());
   *
   * // 2. Open Connect UI
   * await alterConnect.open({
   *   token: session_token,
   *   onSuccess: (grants, completion) => {
   *     console.log('Connected!', grants);
   *     console.log('Providers not kept:', completion.failedGrants);
   *   },
   *   onError: (error) => {
   *     console.error('Failed:', error);
   *   }
   * });
   */
  async open(options: OpenOptions): Promise<void> {
    // Check if SDK has been destroyed
    if (!this._isInitialized) {
      throw this.createError(
        "sdk_destroyed",
        "Cannot call open() - SDK instance has been destroyed",
      );
    }

    debugLog(this.config, "Opening Connect UI");

    // Validate required options
    if (!options.token || typeof options.token !== "string") {
      throw this.createError(
        "invalid_options",
        "Session token is required. Create one from the application backend with an Alter SDK.",
      );
    }

    // The token's prefix picks the hosted page. A token with neither prefix
    // was not issued by an Alter backend, so it is refused here rather than
    // opened as a Connect page that can only report an invalid session.
    const kind = connectSessionKind(options.token);
    if (kind === null) {
      throw this.createError(
        "invalid_options",
        "Unrecognized Connect session token. Pass the sessionToken an Alter SDK returned (sess_… for OAuth Connect, ms_sess_… for managed-secret Connect).",
      );
    }

    if (!options.onSuccess || typeof options.onSuccess !== "function") {
      throw this.createError(
        "invalid_options",
        "onSuccess callback is required",
      );
    }

    // Inline mode: validated with the other options, BEFORE the is-open
    // no-op below, so a bad container is reported rather than swallowed.
    const container = this.resolveContainer(options);

    // Check if already open
    if (this.isOpen()) {
      debugLog(this.config, "Connect UI is already open");
      return;
    }

    // Remember where focus was so close() can hand it back (see
    // `_focusReturnTarget`). Captured for every mode: only an inline flow
    // moves focus into SDK-owned DOM, but the restore is conditional on focus
    // having actually been lost, so recording it is never harmful.
    this._focusReturnTarget =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;

    // Every flow handler reports through the same four callbacks.
    const callbacks = {
      onSuccess: (grants: Grant[], completion: ConnectCompletion) => {
        debugLog(this.config, "OAuth success:", grants);
        this.handleOAuthSuccess(grants, completion);
      },
      onError: (error: AlterError) => {
        debugLog(this.config, "OAuth error:", error);
        this.handleOAuthError(error);
      },
      onCancel: () => {
        debugLog(
          this.config,
          "OAuth cancelled (popup closed or widget dismissed)",
        );
        this.handleOAuthCancel();
      },
      onNotice: (eventName: string, metadata: Record<string, unknown>) => {
        debugLog(this.config, "OAuth notice:", eventName);
        // Non-terminal: goes to the `event` bus (and therefore `onEvent`), not
        // the `error` bus, so the flow stays open and nothing calls close().
        this.eventEmitter.emit("event", eventName, metadata);
      },
      debug: this.config.debug,
    };
    const popupSizing = { popupWidth: 500, popupHeight: 700 };

    // Choose the flow handler ONCE, from the options:
    //   container + single   -> DirectWindowHandler (provider window from the
    //                          click; falls back to the inline tile below if
    //                          the browser blocks the window). OAuth only: a
    //                          managed-secret session has no provider window,
    //                          and its frame opens its own window.
    //   container           -> EmbedHandler (inline tile / picker)
    //   neither             -> OAuthHandler (popup, or redirect on a phone)
    // Constructing it validates baseURL and throws on invalid input, so this
    // happens BEFORE `isOpen` is set: a throw here leaves the SDK closed and
    // reopenable rather than stuck.
    const wantsDirect =
      container !== null && options.singleProvider === true && kind === "oauth";
    const handler: OAuthHandler | EmbedHandler | DirectWindowHandler =
      container === null
        ? new OAuthHandler({
            baseURL: this._baseURL,
            kind,
            ...popupSizing,
            ...callbacks,
          })
        : wantsDirect
          ? new DirectWindowHandler({
              baseURL: this._baseURL,
              container,
              ...popupSizing,
              ...callbacks,
            })
          : new EmbedHandler({
              baseURL: this._baseURL,
              container,
              kind,
              ...callbacks,
            });
    this._flowHandler = handler;

    // Update state (after handler creation succeeds)
    this.stateManager.setState({
      isOpen: true,
      error: null,
      sessionToken: options.token,
    });

    // Register event handlers
    this.registerEventHandlers(options);

    // Build the Connect UI URL — the backend serves the full UI
    const connectURL = buildConnectUrl(
      this._baseURL,
      options.token,
      kind,
      container !== null ? window.location.origin : undefined,
    );

    debugLog(this.config, "Connect URL:", connectURL);

    // Start the flow. `mode` is null when the flow was REFUSED before it
    // opened (popup blocked, redirect unbindable, unparseable URL): the
    // handler has already reported the error and close() has already run, so
    // nothing below may claim the flow opened.
    let mode: "popup" | "redirect" | "embed" | null;
    let direct = false;
    if (handler instanceof DirectWindowHandler) {
      // The direct flow's URL has no embed_origin: the provider window is a
      // top-level window and binds like any popup.
      mode = handler.start(buildConnectUrl(this._baseURL, options.token, kind));
      if (mode !== null) {
        direct = true;
        debugLog(this.config, "Opened the provider window from the click");
      } else {
        // A blocked window means no click or no pop-ups. Not an error: the
        // inline tile gives the user a click of their own. `container` is
        // non-null here by construction of `wantsDirect`.
        debugLog(
          this.config,
          "Provider window blocked; rendering the inline tile instead",
        );
        const tile = new EmbedHandler({
          baseURL: this._baseURL,
          container: container as HTMLElement,
          kind,
          ...callbacks,
        });
        this._flowHandler = tile;
        mode = tile.start(connectURL);
      }
    } else if (handler instanceof EmbedHandler) {
      mode = handler.start(connectURL);
    } else {
      mode = handler.startOAuth(connectURL);
    }

    // Analytics: only a flow that actually opened is `connect_opened`. It is
    // delivered to this open()'s `onEvent` directly (it does not travel the
    // `event` bus, which carries the hosted flow's notices), and a throwing
    // application callback must never reject an `open()` whose flow is
    // already running, so the call is guarded and the throw dropped.
    if (mode !== null) {
      if (options.onEvent) {
        try {
          options.onEvent("connect_opened", {
            timestamp: new Date().toISOString(),
            mode,
            ...(direct ? { direct: true } : {}),
          });
        } catch {
          // Application analytics bug; the flow is unaffected.
        }
      }
      debugLog(this.config, "Connect UI opened successfully");
    }
  }

  /**
   * Validate the optional inline-mode container.
   *
   * Returns the element, or `null` when the caller did not ask for inline
   * mode. Rejections are launch-time errors (like `invalid_options`): a
   * detached element would mount an iframe nobody can see; a host page with
   * no http(s) origin (`file://`, a sandboxed frame reporting `"null"`) can
   * never be named as the frame's allowed ancestor; and a host page that is
   * itself inside an iframe cannot host the widget either, because the hosted
   * page names exactly ONE framing origin and the browser checks every
   * ancestor in the chain, so the outer page is always the one refused. That
   * last case is otherwise invisible from here (a cross-origin frame exposes
   * no load failure), which is why it is refused up front.
   */
  private resolveContainer(options: OpenOptions): HTMLElement | null {
    if (!("container" in options) || options.container === undefined) {
      return null;
    }
    const container = options.container;
    if (
      container === null ||
      typeof container !== "object" ||
      (container as Node).nodeType !== 1 ||
      !(container as Element).isConnected
    ) {
      throw this.createError(
        "invalid_container",
        "container must be an Element attached to the document",
      );
    }
    if (!/^https?:/.test(window.location.origin)) {
      throw this.createError(
        "embed_unsupported_origin",
        "Inline mode needs the host page to have an http(s) origin",
      );
    }
    if (window.self !== window.top) {
      throw this.createError(
        "embed_unsupported_origin",
        "Inline mode needs the host page to be the top-level window. This " +
          "page is itself inside an iframe, and the embedded Connect page " +
          "can be framed by exactly one origin, so the browser would refuse " +
          "it here. Open the popup flow instead (omit container).",
      );
    }
    return container;
  }

  /**
   * Close the Connect UI
   */
  close(): void {
    // Check if SDK has been destroyed
    if (!this._isInitialized) {
      throw this.createError(
        "sdk_destroyed",
        "Cannot call close() - SDK instance has been destroyed",
      );
    }

    debugLog(this.config, "Closing Connect UI");
    const wasOpen = this.stateManager.get("isOpen");

    // Clean up OAuth handler if active
    if (this._flowHandler) {
      this._flowHandler.close();
      this._flowHandler = null;
    }

    // Remove only per-open handlers (preserves global handlers from on())
    this._perOpenCleanups.forEach((cleanup) => cleanup());
    this._perOpenCleanups = [];

    // Update state
    this.stateManager.setState({ isOpen: false });

    // The handler has removed any DOM it mounted; give focus back if that
    // removal dropped it.
    this.restoreFocus();

    // `close` marks the open -> closed transition, once. A close() on an
    // already-closed SDK is a no-op and stays silent, so a listener counting
    // closes counts flows, not calls.
    if (wasOpen) {
      this.eventEmitter.emit("close");
    }
  }

  /**
   * Return focus to the element that had it at `open()`, but only when focus
   * has actually been lost to the body: if the user (or the application)
   * moved focus somewhere meaningful in the meantime, stealing it back would
   * be its own accessibility bug.
   */
  private restoreFocus(): void {
    const target = this._focusReturnTarget;
    this._focusReturnTarget = null;
    if (target === null || !target.isConnected) {
      return;
    }
    const active = document.activeElement;
    if (active !== null && active !== document.body) {
      return;
    }
    target.focus();
  }

  /**
   * Destroy the SDK instance and clean up resources
   */
  destroy(): void {
    if (!this._isInitialized) {
      // Already destroyed, silently return
      return;
    }

    debugLog(this.config, "Destroying SDK instance");

    // Clean up OAuth handler if active
    if (this._flowHandler) {
      this._flowHandler.close();
      this._flowHandler = null;
    }

    // Update state
    if (this.stateManager.get("isOpen")) {
      this.stateManager.setState({ isOpen: false });
    }
    this.restoreFocus();

    // Clean up resources
    this.pendingRedirectOutcome = null;
    this.eventEmitter.removeAllListeners();
    this.stateManager.clearListeners();
    this._isInitialized = false;
  }

  /**
   * Register an event listener
   */
  on(event: string, handler: EventHandler): () => void {
    // Check if SDK has been destroyed
    if (!this._isInitialized) {
      throw this.createError(
        "sdk_destroyed",
        "Cannot call on() - SDK instance has been destroyed",
      );
    }

    const unsubscribe = this.eventEmitter.on(event, handler);
    // A mobile redirect outcome resolved during construction, before this
    // subscription could exist. Hand it over now.
    this.flushRedirectOutcome(event);
    return unsubscribe;
  }

  /**
   * Remove an event listener
   */
  off(event: string, handler: EventHandler): void {
    // Check if SDK has been destroyed
    if (!this._isInitialized) {
      throw this.createError(
        "sdk_destroyed",
        "Cannot call off() - SDK instance has been destroyed",
      );
    }

    this.eventEmitter.off(event, handler);
  }

  /**
   * Check if Connect UI is open
   */
  isOpen(): boolean {
    // Check if SDK has been destroyed
    if (!this._isInitialized) {
      throw this.createError(
        "sdk_destroyed",
        "Cannot call isOpen() - SDK instance has been destroyed",
      );
    }

    return this.stateManager.get("isOpen");
  }

  /**
   * Get SDK version
   */
  getVersion(): string {
    return VERSION;
  }

  /**
   * Clean up OAuth handler reference after a flow completes.
   * The OAuthHandler.close() is already called internally by the handler
   * before invoking callbacks, so we only need to null the reference here.
   */
  private cleanupHandler(): void {
    this._flowHandler = null;
  }

  /**
   * Handle OAuth success
   */
  private handleOAuthSuccess(
    grants: Grant[],
    completion: ConnectCompletion,
  ): void {
    this.cleanupHandler();
    this.eventEmitter.emit("success", grants, completion);
  }

  /**
   * Handle OAuth error
   */
  private handleOAuthError(error: AlterError): void {
    this.cleanupHandler();
    this.stateManager.setState({ error });
    this.eventEmitter.emit("error", error);
  }

  /**
   * Handle OAuth cancellation (user closed the popup, or pressed Cancel in
   * the inline widget)
   */
  private handleOAuthCancel(): void {
    this.cleanupHandler();
    this.eventEmitter.emit("exit");
  }

  /**
   * Invoke a terminal application callback, then ALWAYS release the flow.
   *
   * `close()` is what resets `isOpen`, releases the per-open listeners, and
   * tears down the popup. Running it as the statement *after* the application
   * callback makes the SDK's own cleanup contingent on application code not
   * throwing, and `EventEmitter.emit` swallows that throw — so the failure is
   * invisible, `isOpen` stays `true` for the life of the page, and every later
   * `open()` returns at its own is-open guard. A one-line application bug
   * (a `setState` on an unmounted component, a property read on an undefined
   * grant field) would brick the widget with nothing surfaced anywhere. That is
   * the same dead-widget outcome as gating handler REGISTRATION on the optional
   * callback, reached by a different route, so it gets the same treatment:
   * host code must never be able to corrupt the SDK's lifecycle state.
   *
   * The `_isInitialized` guard covers a callback that calls `destroy()` — that
   * teardown already reset `isOpen` and dropped the listeners, so there is
   * nothing left to release and an unguarded `close()` would raise
   * `sdk_destroyed` on top of (and masking) the application's own exception.
   */
  private releaseAfterTerminal(invokeCallback: () => void): void {
    try {
      invokeCallback();
    } finally {
      if (this._isInitialized) {
        this.close();
      }
    }
  }

  /**
   * Register event handlers from OpenOptions
   */
  private registerEventHandlers(options: OpenOptions): void {
    // Terminal handlers are registered UNCONDITIONALLY — every one of them is
    // what runs close(), and close() is what resets `isOpen`, releases the
    // per-open listeners, and tears down the popup. Gating registration on the
    // optional application callback (as the exit handler once did) leaves a
    // flow that ended with the user closing the popup stuck at `isOpen: true`
    // forever, so every later open() silently returns at its own is-open guard
    // and the widget can never be reopened. `onSuccess` is typed as required,
    // but TypeScript erases at runtime and a JS caller can omit it, so it gets
    // the same treatment rather than trusting the type.
    //
    // Each one runs through `releaseAfterTerminal`, so the release is bound to
    // the transition rather than to the application callback returning.
    this._perOpenCleanups.push(
      this.eventEmitter.on(
        "success",
        (grants: Grant[], completion: ConnectCompletion) => {
          this.releaseAfterTerminal(() =>
            options.onSuccess?.(grants, completion),
          );
        },
      ),
    );

    this._perOpenCleanups.push(
      this.eventEmitter.on("exit", () => {
        this.releaseAfterTerminal(() => options.onExit?.());
      }),
    );

    // Error handler — always registered to ensure close() is called
    this._perOpenCleanups.push(
      this.eventEmitter.on("error", (error: AlterError) => {
        this.releaseAfterTerminal(() => options.onError?.(error));
      }),
    );

    // Event handler (analytics)
    if (options.onEvent) {
      this._perOpenCleanups.push(
        this.eventEmitter.on(
          "event",
          (eventName: string, metadata: Record<string, unknown>) => {
            options.onEvent!(eventName, metadata);
          },
        ),
      );
    }
  }

  /**
   * Create an AlterError object
   */
  private createError(
    code: string,
    message: string,
    details?: Record<string, unknown>,
  ): Error {
    const error = new Error(message) as Error & {
      code?: string;
      details?: Record<string, unknown>;
    };
    error.code = code;
    error.details = details;
    return error;
  }
}
